import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  AdminFormPage,
  saveErrorToast,
  useAdminFormPersistence,
} from "@components/CatalogIndex";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import {
  useV2GetQuestionnaireByIdQuery,
  useV2ListTeamsForAccountQuery,
} from "@generated";
import { SurveyForm } from "../SurveyForm/SurveyForm";
import { QuestionsTab } from "../tabs/QuestionsTab/QuestionsTab";
import { LookupsTab } from "../tabs/LookupsTab/LookupsTab";
import { CalculationsTab } from "../tabs/CalculationsTab/CalculationsTab";
import { RecommendationsTab } from "../tabs/RecommendationsTab/RecommendationsTab";
import { useQuestionnaireMutations } from "../api";
import {
  emptySurveyForm,
  QUESTIONNAIRE_EDIT_TABS,
  type QuestionnaireEditTab,
  type SurveyFormValues,
} from "../types";

const FORM_ID = "survey-form";

export const Edit = (): JSX.Element => {
  const navigate = useNavigate();
  const { id = "" } = useParams();
  const { update } = useQuestionnaireMutations();
  const [activeTab, setActiveTab] = useState<QuestionnaireEditTab>("survey");

  const { data, isLoading, refetch } = useV2GetQuestionnaireByIdQuery(
    { id: id },
    { refetchOnMountOrArgChange: true }
  );
  // Large page so the teams multi-select isn't silently truncated to the
  // API's default page size.
  const { data: teamsData } = useV2ListTeamsForAccountQuery({
    page: { size: 500 },
  });

  const { toast, setToast, savedInitial, formKey, markSaved } =
    useAdminFormPersistence<SurveyFormValues>();
  const [submitting, setSubmitting] = useState(false);

  const teamOptions = useMemo(
    () =>
      (teamsData?.data ?? []).map((t: any) => ({
        value: String(t.id),
        label: t.attributes?.name ?? "",
      })),
    [teamsData]
  );

  const loadedInitial = useMemo<SurveyFormValues>(() => {
    const attrs: any = data?.data?.attributes;
    if (!attrs) return emptySurveyForm;
    const completionUrl = attrs["completion-url"] ?? "";
    return {
      name: attrs.name ?? "",
      published: !!attrs.published,
      introduction: attrs.introduction ?? "",
      thankYou: attrs["thank-you"] ?? "",
      redirect: !!completionUrl,
      completionUrl,
      tagList: attrs["tag-list"] ?? [],
      teamIds: (attrs.teams ?? []).map((t: any) => String(t.id)),
    };
  }, [data]);

  if (isLoading) return <ScopeStackSpinner />;

  const initialValues = savedInitial ?? loadedInitial;
  const title = `Edit ${initialValues.name || "Questionnaire"}`;

  const handleSubmit = async (values: SurveyFormValues) => {
    setSubmitting(true);
    try {
      await update(id, values);
      markSaved(values, `'${values.name}' saved`);
      // Generated API has no cache tags; refetch so the next open is fresh.
      refetch();
    } catch (err) {
      console.error("Failed to update questionnaire", err);
      setToast(saveErrorToast(err, "questionnaire"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="questionnaire-edit-layout">
      <AdminFormPage
        title={title}
        formId={activeTab === "survey" ? FORM_ID : undefined}
        onCancel={() => navigate("../..", { relative: "path" })}
        isSubmitting={submitting}
        tabs={QUESTIONNAIRE_EDIT_TABS}
        activeTab={activeTab}
        onTabChange={(key) => setActiveTab(key as QuestionnaireEditTab)}
      >
        {activeTab === "survey" && (
          <SurveyForm
            key={formKey}
            id={FORM_ID}
            initialValues={initialValues}
            teamOptions={teamOptions}
            isSubmitting={submitting}
            onSubmit={handleSubmit}
          />
        )}
        {activeTab === "questions" && <QuestionsTab questionnaireId={id} />}
        {activeTab === "lookups" && <LookupsTab questionnaireId={id} />}
        {activeTab === "calculations" && (
          <CalculationsTab questionnaireId={id} />
        )}
        {activeTab === "recommendations" && (
          <RecommendationsTab questionnaireId={id} />
        )}
      </AdminFormPage>

      {toast && (
        <ToastAlert
          variant={toast.variant}
          text={toast.text}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default Edit;
